var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
// SPDX-License-Identifier: AGPL-3.0-only
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { ApolloError, useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { EXECUTE_PLUGIN, SET_PLUGIN_STATE, TENANT_PLUGINS } from "@/queries/PluginPlatform";
import { PluginErrorCode, PluginRequestError, errorMessage, } from "./contracts";
var unavailable = function () { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        throw new PluginRequestError("Plugin host is unavailable", PluginErrorCode.Unavailable);
    });
}); };
var PluginContext = createContext({
    plugins: [],
    loading: true,
    error: "",
    can: function () { return false; },
    refresh: unavailable,
    setState: unavailable,
    execute: unavailable,
});
export var usePlugins = function () { return useContext(PluginContext); };
var codes = new Set(Object.values(PluginErrorCode));
/** Hasura forwards the gateway's message and extensions; no database details are exposed. */
export var pluginError = function (error) {
    var _a, _b, _c;
    if (error instanceof PluginRequestError)
        return error;
    var graph = error instanceof ApolloError ? error.graphQLErrors[0] : undefined;
    var code = String((_b = (_a = graph === null || graph === void 0 ? void 0 : graph.extensions) === null || _a === void 0 ? void 0 : _a.code) !== null && _b !== void 0 ? _b : "");
    return new PluginRequestError((graph === null || graph === void 0 ? void 0 : graph.message) || errorMessage(error), codes.has(code) ? code : PluginErrorCode.Unavailable, (_c = graph === null || graph === void 0 ? void 0 : graph.extensions) === null || _c === void 0 ? void 0 : _c.current);
};
/**
 * Loads the tenant's plugins and runs their operations. Requests are scoped to
 * the authenticated tenant; a response that arrives after the tenant changed
 * is discarded.
 */
export var PluginProvider = function (_a) {
    var children = _a.children;
    var client = useApolloClient();
    var auth = useContext(AuthContext);
    var t = useTranslation().t;
    var tenant = useTenantStore()[0];
    var scope = tenant && tenant === auth.tenantId && auth.isAuthenticated ? tenant : "";
    var currentScope = useRef(scope);
    currentScope.current = scope;
    var _b = useState({
        scope: "",
        plugins: [],
    }), result = _b[0], setResult = _b[1];
    var _c = useState(true), loading = _c[0], setLoading = _c[1];
    var _d = useState(""), error = _d[0], setError = _d[1];
    var hasRole = auth.hasRole, updateTokenAndPermissionLabels = auth.updateTokenAndPermissionLabels;
    var refresh = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var response, cause_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!scope) {
                        setLoading(false);
                        return [2 /*return*/];
                    }
                    setLoading(true);
                    setError("");
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, client.query({
                            query: TENANT_PLUGINS,
                            fetchPolicy: "no-cache",
                        })];
                case 2:
                    response = _a.sent();
                    if (currentScope.current === scope) {
                        setResult({ scope: scope, plugins: response.data.tenant_plugins.data.plugins });
                    }
                    return [3 /*break*/, 5];
                case 3:
                    cause_1 = _a.sent();
                    if (currentScope.current === scope)
                        setError(pluginError(cause_1).message);
                    return [3 /*break*/, 5];
                case 4:
                    if (currentScope.current === scope)
                        setLoading(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [client, scope]);
    useEffect(function () {
        void refresh();
    }, [refresh]);
    var execute = useCallback(function (id, operation, data) { return __awaiter(void 0, void 0, void 0, function () {
        var response, cause_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!scope)
                        throw new PluginRequestError(t("plugins.signIn"), PluginErrorCode.Forbidden);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, client.mutate({
                            mutation: EXECUTE_PLUGIN,
                            variables: { plugin_id: id, operation: operation, data: data },
                        })];
                case 2:
                    response = _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    cause_2 = _a.sent();
                    throw pluginError(cause_2);
                case 4:
                    if (scope !== currentScope.current) {
                        throw new PluginRequestError(t("plugins.signIn"), PluginErrorCode.Forbidden);
                    }
                    if (!response.data)
                        throw new PluginRequestError("Empty plugin response", PluginErrorCode.Unavailable);
                    return [2 /*return*/, response.data.execute_plugin_operation.data];
            }
        });
    }); }, [client, scope, t]);
    var setState = useCallback(function (plugin, next) { return __awaiter(void 0, void 0, void 0, function () {
        var cause_3;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!scope)
                        throw new PluginRequestError(t("plugins.signIn"), PluginErrorCode.Forbidden);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, client.mutate({
                            mutation: SET_PLUGIN_STATE,
                            variables: {
                                plugin_id: plugin.manifest.id,
                                state: next,
                                expected_version: plugin.version,
                            },
                        })];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    cause_3 = _a.sent();
                    throw pluginError(cause_3);
                case 4:
                    // Turning a plugin on grants new roles; refresh the token to receive them.
                    updateTokenAndPermissionLabels();
                    return [4 /*yield*/, refresh()];
                case 5:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [client, refresh, scope, t, updateTokenAndPermissionLabels]);
    var can = useCallback(function (permission) { return Boolean(scope) && hasRole(permission); }, [hasRole, scope]);
    var value = useMemo(function () { return ({
        plugins: result.scope === scope ? result.plugins : [],
        loading: loading,
        error: scope ? error : t("plugins.signIn"),
        can: can,
        refresh: refresh,
        execute: execute,
        setState: setState,
    }); }, [can, error, execute, loading, refresh, result, scope, setState, t]);
    return _jsx(PluginContext.Provider, { value: value, children: children });
};
